import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Row, Col, Card, Table, Tag, Statistic, Switch, Button,
  Tooltip, Descriptions, Progress, Modal, Tabs, Space, Badge,
  Select, Empty, Collapse, Spin, Alert,
} from 'antd';
import {
  DatabaseOutlined, ThunderboltOutlined,
  ClockCircleOutlined, UserOutlined, StopOutlined,
  CheckCircleOutlined, CloseCircleOutlined, WarningOutlined,
  SettingOutlined, SyncOutlined, DashboardOutlined,
  CloudServerOutlined, HddOutlined, FundOutlined,
} from '@ant-design/icons';
import { MYSQL_REPLICA_PRIORITY_IDS } from '../utils/mysqlReplicaServers';
import {
  useMysqlServersQuery,
} from '../store/api';

const API_BASE = 'https://activity.smartandsafe.com.co/api';

const REFRESH_INTERVALS = [
  { label: '1s', value: 1000 },
  { label: '3s', value: 3000 },
  { label: '5s', value: 5000 },
  { label: '10s', value: 10000 },
  { label: '30s', value: 30000 },
];

const formatBytes = (bytes) => {
  if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return bytes + ' B';
};

const formatUptime = (seconds) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatTime = (seconds) => {
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${seconds}s`;
};

// ─── Individual Server Monitor Panel ──────────
const ServerMonitorPanel = ({ serverId, serverName, serverIp, interval, autoRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [statusData, setStatusData] = useState(null);
  const [processData, setProcessData] = useState(null);
  const [varsData, setVarsData] = useState(null);
  const [replData, setReplData] = useState(null);
  const [loading, setLoading] = useState({});
  const [error, setError] = useState(null);

  const prevStatusRef = useRef(null);
  const prevTimeRef = useRef(null);
  const [deltas, setDeltas] = useState({});
  const intervalRef = useRef(null);
  const varsIntervalRef = useRef(null);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    return {
      'X-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    };
  }, []);

  const fetchData = useCallback(async (endpoint) => {
    const sid = serverId || '';
    const sep = sid ? '?server_id=' + sid : '';
    const resp = await fetch(`${API_BASE}/mysql/${endpoint}${sep}`, { headers: getHeaders() });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${resp.status}`);
    }
    return resp.json();
  }, [serverId, getHeaders]);

  const refreshStatus = useCallback(async () => {
    try {
      setLoading(l => ({ ...l, status: true }));
      const data = await fetchData('status');
      setStatusData(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(l => ({ ...l, status: false }));
    }
  }, [fetchData]);

  const refreshProcesslist = useCallback(async () => {
    try {
      setLoading(l => ({ ...l, process: true }));
      const data = await fetchData('processlist');
      setProcessData(data);
    } catch (e) { /* status handles errors */ }
    finally { setLoading(l => ({ ...l, process: false })); }
  }, [fetchData]);

  const refreshReplication = useCallback(async () => {
    try {
      const data = await fetchData('replication');
      setReplData(data);
    } catch (e) { /* ignore */ }
  }, [fetchData]);

  const refreshVariables = useCallback(async () => {
    try {
      const data = await fetchData('variables');
      setVarsData(data);
    } catch (e) { /* ignore */ }
  }, [fetchData]);

  const refreshAll = useCallback(() => {
    refreshStatus();
    refreshProcesslist();
    refreshReplication();
  }, [refreshStatus, refreshProcesslist, refreshReplication]);

  // Initial load
  useEffect(() => {
    refreshAll();
    refreshVariables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId]);

  // Polling
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh && interval > 0) {
      intervalRef.current = setInterval(refreshAll, interval);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, interval, refreshAll]);

  // Variables polling (slow)
  useEffect(() => {
    if (varsIntervalRef.current) clearInterval(varsIntervalRef.current);
    varsIntervalRef.current = setInterval(refreshVariables, 60000);
    return () => { if (varsIntervalRef.current) clearInterval(varsIntervalRef.current); };
  }, [refreshVariables]);

  // Delta calculation
  useEffect(() => {
    if (statusData?.status) {
      const now = Date.now();
      const prev = prevStatusRef.current;
      const prevTime = prevTimeRef.current;

      if (prev && prevTime) {
        const elapsed = (now - prevTime) / 1000;
        if (elapsed > 0.5) {
          const s = statusData.status;
          setDeltas({
            qps: ((s.Questions - prev.Questions) / elapsed).toFixed(1),
            reads: ((s.Com_select - prev.Com_select) / elapsed).toFixed(1),
            writes: (((s.Com_insert + s.Com_update + s.Com_delete) - (prev.Com_insert + prev.Com_update + prev.Com_delete)) / elapsed).toFixed(1),
            bytesIn: ((s.Bytes_received - prev.Bytes_received) / elapsed),
            bytesOut: ((s.Bytes_sent - prev.Bytes_sent) / elapsed),
            slowQueries: s.Slow_queries - prev.Slow_queries,
          });
        }
      }
      prevStatusRef.current = statusData.status;
      prevTimeRef.current = now;
    }
  }, [statusData]);

  const handleKill = useCallback(async (threadId) => {
    Modal.confirm({
      title: `¿Matar thread #${threadId}?`,
      content: `Servidor: ${serverName}`,
      okText: 'Kill',
      okType: 'danger',
      onOk: async () => {
        const sid = serverId || '';
        const sep = sid ? '?server_id=' + sid : '';
        await fetch(`${API_BASE}/mysql/kill/${threadId}${sep}`, {
          method: 'POST',
          headers: getHeaders(),
        });
        refreshProcesslist();
      },
    });
  }, [serverId, serverName, getHeaders, refreshProcesslist]);

  const status = statusData?.status || {};
  const serverInfo = statusData?.serverInfo || null;
  const processes = processData?.processes || [];
  const variables = varsData?.variables || {};
  const replication = replData?.replication || {};

  // Parse load average: "3.07, 2.21, 1.70" → [3.07, 2.21, 1.70]
  const loadAvg = serverInfo?.carga ? serverInfo.carga.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v)) : [];
  const loadCurrent = loadAvg[0] || null;
  const cores = serverInfo?.nucleos ? parseInt(serverInfo.nucleos) : null;
  const loadPct = (loadCurrent && cores) ? Math.min(Math.round((loadCurrent / cores) * 100), 100) : null;

  const activeProcesses = processes.filter(p =>
    p.command !== 'Sleep' && p.command !== 'Daemon' && p.command !== 'Binlog Dump'
  );
  const longRunning = processes.filter(p => p.time > 30 && p.command !== 'Sleep' && p.command !== 'Daemon');

  if (error) {
    return (
      <Alert
        message={`Error conectando a ${serverName}`}
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 12 }}
      />
    );
  }

  if (!statusData) {
    return <div style={{ textAlign: 'center', padding: 40 }}><Spin tip={`Conectando a ${serverName}...`} /></div>;
  }

  const kpiStyle = { borderRadius: 12, height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };

  const processColumns = [
    { title: 'ID', dataIndex: 'id', width: 70, sorter: (a, b) => a.id - b.id },
    {
      title: 'User', dataIndex: 'user', width: 100,
      render: (v) => <span><UserOutlined style={{ marginRight: 4 }} />{v}</span>,
    },
    {
      title: 'Host', dataIndex: 'host', width: 140, ellipsis: true,
      render: (v) => <Tooltip title={v}>{v?.split(':')[0]}</Tooltip>,
    },
    {
      title: 'DB', dataIndex: 'db', width: 100,
      render: (v) => v || <span style={{ color: '#8392a5' }}>—</span>,
    },
    {
      title: 'Cmd', dataIndex: 'command', width: 90,
      render: (v) => {
        const colors = { Query: 'blue', Sleep: 'default', Execute: 'green', 'Binlog Dump': 'purple' };
        return <Tag color={colors[v] || 'default'}>{v}</Tag>;
      },
    },
    {
      title: 'Time', dataIndex: 'time', width: 80,
      sorter: (a, b) => a.time - b.time,
      defaultSortOrder: 'descend',
      render: (v) => {
        const color = v > 300 ? '#dc3545' : v > 30 ? '#fd7e14' : '#22c55e';
        return <span style={{ color, fontWeight: v > 30 ? 700 : 400, fontFamily: 'monospace' }}>{formatTime(v)}</span>;
      },
    },
    {
      title: 'State', dataIndex: 'state', width: 130, ellipsis: true,
      render: (v) => v || <span style={{ color: '#8392a5' }}>—</span>,
    },
    {
      title: 'Query', dataIndex: 'info', ellipsis: true,
      render: (v) => v ? (
        <Tooltip title={<pre style={{ maxHeight: 300, overflow: 'auto', margin: 0, fontSize: 11 }}>{v}</pre>} overlayStyle={{ maxWidth: 600 }}>
          <code style={{ fontSize: 11, color: '#8392a5' }}>{v.substring(0, 80)}{v.length > 80 ? '…' : ''}</code>
        </Tooltip>
      ) : <span style={{ color: '#8392a5' }}>—</span>,
    },
    {
      title: '', width: 44, fixed: 'right',
      render: (_, record) => record.command !== 'Daemon' && record.command !== 'Binlog Dump' ? (
        <Tooltip title="Kill">
          <Button type="text" danger size="small" icon={<StopOutlined />}
            onClick={() => handleKill(record.id)} />
        </Tooltip>
      ) : null,
    },
  ];

  const variableRows = Object.entries(variables).map(([key, value]) => ({
    key, name: key,
    value: key.includes('size') || key.includes('buffer') ? formatBytes(Number(value) || 0) : value,
    raw: value,
  }));

  const variableColumns = [
    { title: 'Variable', dataIndex: 'name', render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Value', dataIndex: 'value', render: (v) => <strong>{v}</strong> },
  ];

  const renderOverview = () => (
    <>
      {/* Server system metrics row */}
      {serverInfo && (
        <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
          <Col xs={12} sm={8} lg={6}>
            <Card size="small" style={{ ...kpiStyle, borderLeft: `3px solid ${loadPct > 80 ? '#dc3545' : loadPct > 50 ? '#fd7e14' : '#22c55e'}` }}>
              <Statistic title="CPU Load" value={loadCurrent ?? '—'}
                suffix={cores ? `/ ${cores} cores` : ''}
                prefix={<FundOutlined />}
                valueStyle={{ color: loadPct > 80 ? '#dc3545' : loadPct > 50 ? '#fd7e14' : '#22c55e', fontSize: 22 }} />
              {loadPct !== null && (
                <Progress percent={loadPct} size="small" strokeColor={loadPct > 80 ? '#dc3545' : loadPct > 50 ? '#fd7e14' : '#22c55e'}
                  style={{ marginTop: 4 }} />
              )}
              {loadAvg.length === 3 && (
                <div style={{ fontSize: 11, color: '#8392a5', marginTop: 2 }}>
                  1m: {loadAvg[0]} &nbsp;5m: {loadAvg[1]} &nbsp;15m: {loadAvg[2]}
                </div>
              )}
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <Card size="small" style={kpiStyle}>
              <Statistic title="Disco" value={serverInfo.espaciolibre || '—'}
                suffix={serverInfo.disco ? `/ ${serverInfo.disco}` : ''}
                prefix={<HddOutlined />}
                valueStyle={{ fontSize: 20 }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <Card size="small" style={kpiStyle}>
              <Statistic title="RAM" value={serverInfo.ram || '—'}
                prefix={<CloudServerOutlined />}
                valueStyle={{ fontSize: 20 }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={6}>
            <Card size="small" style={kpiStyle}>
              <Statistic title="Server Uptime" value={serverInfo.uptime ? serverInfo.uptime.split(',')[0] : '—'}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ fontSize: 16 }} />
            </Card>
          </Col>
        </Row>
      )}

      {/* MySQL metrics row */}
      <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={kpiStyle}>
            <Statistic title="QPS" value={deltas.qps || status._qps || 0}
              prefix={<ThunderboltOutlined />} valueStyle={{ color: '#0168fa', fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={kpiStyle}>
            <Statistic title="Reads/s" value={deltas.reads || 0}
              prefix={<DatabaseOutlined />} valueStyle={{ color: '#22c55e', fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={kpiStyle}>
            <Statistic title="Writes/s" value={deltas.writes || 0}
              prefix={<DatabaseOutlined />} valueStyle={{ color: '#fd7e14', fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={kpiStyle}>
            <Statistic title="Active" value={status.Threads_running || 0}
              suffix={`/ ${status.Threads_connected || 0}`}
              valueStyle={{ color: status.Threads_running > 20 ? '#dc3545' : '#0ea5e9', fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={kpiStyle}>
            <Statistic title="Conns" value={status.Threads_connected || 0}
              suffix={`/ ${variables.max_connections || '?'}`}
              valueStyle={{ fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={kpiStyle}>
            <Statistic title="Uptime" value={formatUptime(status.Uptime || 0)}
              prefix={<ClockCircleOutlined />} valueStyle={{ fontSize: 18 }} />
          </Card>
        </Col>
      </Row>
      <Row gutter={[10, 10]}>
        <Col xs={24} md={8}>
          <Card size="small" title="Buffer Pool" style={kpiStyle}>
            <Progress percent={status._buffer_pool_usage_pct || 0} strokeColor="#0168fa" size="small"
              format={(p) => `${p}%`} />
            <div style={{ fontSize: 11, color: '#8392a5', marginTop: 4 }}>
              Hit: <strong style={{ color: (status._buffer_pool_hit_rate || 0) > 99 ? '#22c55e' : '#fd7e14' }}>
                {status._buffer_pool_hit_rate || 0}%</strong>
              &nbsp;| Dirty: <strong>{status.Innodb_buffer_pool_pages_dirty || 0}</strong>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" title="Traffic" style={kpiStyle}>
            <Row gutter={8}>
              <Col span={12}>
                <Statistic title="In/s" value={deltas.bytesIn ? formatBytes(deltas.bytesIn) + '/s' : '—'} valueStyle={{ fontSize: 14 }} />
              </Col>
              <Col span={12}>
                <Statistic title="Out/s" value={deltas.bytesOut ? formatBytes(deltas.bytesOut) + '/s' : '—'} valueStyle={{ fontSize: 14 }} />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" title="Queries" style={kpiStyle}>
            <Row gutter={4}>
              <Col span={6}><Statistic title="SEL" value={status.Com_select || 0} valueStyle={{ fontSize: 12 }} /></Col>
              <Col span={6}><Statistic title="INS" value={status.Com_insert || 0} valueStyle={{ fontSize: 12 }} /></Col>
              <Col span={6}><Statistic title="UPD" value={status.Com_update || 0} valueStyle={{ fontSize: 12 }} /></Col>
              <Col span={6}><Statistic title="DEL" value={status.Com_delete || 0} valueStyle={{ fontSize: 12 }} /></Col>
            </Row>
            <div style={{ fontSize: 11, color: '#8392a5', marginTop: 4 }}>
              Slow: <strong style={{ color: status.Slow_queries > 0 ? '#dc3545' : '#22c55e' }}>{status.Slow_queries || 0}</strong>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderReplication = () => (
    <div>
      {!replication.is_slave && !replication.is_master ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#8392a5' }}>
          <DatabaseOutlined style={{ fontSize: 36, marginBottom: 8 }} />
          <p style={{ margin: 0 }}>Sin replicación configurada</p>
        </div>
      ) : (
        <>
          {replication.is_slave && replication.slave && (
            <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 3 }}
              title={
                <Space>
                  <SyncOutlined spin={replication.slave.io_running === 'Yes'} />
                  <span>Slave</span>
                  {replication.slave.io_running === 'Yes' && replication.slave.sql_running === 'Yes' ? (
                    <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 11 }}>OK</Tag>
                  ) : (
                    <Tag icon={<CloseCircleOutlined />} color="error" style={{ fontSize: 11 }}>Stopped</Tag>
                  )}
                </Space>
              }>
              <Descriptions.Item label="IO">{replication.slave.io_running}</Descriptions.Item>
              <Descriptions.Item label="SQL">{replication.slave.sql_running}</Descriptions.Item>
              <Descriptions.Item label="Behind">
                <span style={{
                  color: replication.slave.seconds_behind === null ? '#8392a5'
                    : replication.slave.seconds_behind > 300 ? '#dc3545'
                    : replication.slave.seconds_behind > 30 ? '#fd7e14' : '#22c55e',
                  fontWeight: 700,
                }}>
                  {replication.slave.seconds_behind ?? 'NULL'}s
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Master">{replication.slave.master_host}:{replication.slave.master_port}</Descriptions.Item>
              <Descriptions.Item label="Log">{replication.slave.master_log_file}</Descriptions.Item>
              <Descriptions.Item label="Pos">{replication.slave.read_master_log_pos}</Descriptions.Item>
              {replication.slave.last_error && (
                <Descriptions.Item label="Error" span={3}>
                  <Tag color="error"><WarningOutlined /> {replication.slave.last_error}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
          {replication.is_master && replication.master && (
            <Descriptions size="small" bordered title="Master" style={{ marginTop: replication.is_slave ? 12 : 0 }}>
              <Descriptions.Item label="Binlog">{replication.master.file}</Descriptions.Item>
              <Descriptions.Item label="Position">{replication.master.position}</Descriptions.Item>
            </Descriptions>
          )}
        </>
      )}
    </div>
  );

  const tabItems = [
    {
      key: 'overview',
      label: <span><DashboardOutlined /> Overview</span>,
      children: renderOverview(),
    },
    {
      key: 'processlist',
      label: <span><ThunderboltOutlined /> Procesos <Badge count={activeProcesses.length} size="small" style={{ marginLeft: 4 }} /></span>,
      children: (
        <div>
          {longRunning.length > 0 && (
            <div style={{ marginBottom: 8, padding: '6px 10px', background: '#fff3cd', borderRadius: 6, border: '1px solid #fcd34d', fontSize: 12 }}>
              <WarningOutlined style={{ color: '#fd7e14', marginRight: 6 }} />
              <strong>{longRunning.length}</strong> queries &gt; 30s
            </div>
          )}
          <Table
            dataSource={processes}
            columns={processColumns}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ x: 800 }}
            rowClassName={(record) =>
              record.time > 300 ? 'row-danger' : record.time > 30 ? 'row-warning' : ''
            }
            loading={loading.process}
          />
        </div>
      ),
    },
    {
      key: 'replication',
      label: <span><SyncOutlined /> Replicación {replication.is_slave && (
        <Badge status={replication.slave?.io_running === 'Yes' ? 'success' : 'error'} style={{ marginLeft: 4 }} />
      )}</span>,
      children: renderReplication(),
    },
    {
      key: 'variables',
      label: <span><SettingOutlined /> Variables</span>,
      children: (
        <Table
          dataSource={variableRows}
          columns={variableColumns}
          size="small"
          pagination={false}
        />
      ),
    },
  ];

  return (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      items={tabItems}
      size="small"
      style={{ marginTop: -4 }}
    />
  );
};

// ─── Main Page Component ────────────────────────
const MonitorMySQL = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [interval, setRefreshInterval] = useState(3000);
  const [selectedServers, setSelectedServers] = useState([0]); // 0 = default/local DB

  const { data: serversData, isLoading: loadingServers } = useMysqlServersQuery(undefined, {
    pollingInterval: 60000,
  });

  const servers = useMemo(() => serversData?.servers || [], [serversData]);

  // Auto-seleccionar réplicas conocidas (incl. Servi master/slave)
  useEffect(() => {
    if (servers.length > 0 && selectedServers.length === 0) {
      const replicaIds = MYSQL_REPLICA_PRIORITY_IDS.filter((id) =>
        servers.some((s) => s.id === id),
      );
      setSelectedServers(replicaIds.length > 0 ? replicaIds : [servers[0].id]);
    }
  }, [servers, selectedServers.length]);

  const collapseItems = selectedServers.map((sId) => {
    const server = servers.find(s => s.id === sId);
    const label = server ? `${server.nombre} (${server.ip})` : `Server #${sId}`;
    return {
      key: String(sId),
      label: (
        <Space>
          <CloudServerOutlined />
          <strong>{label}</strong>
          {server?.mysql_user && <Tag style={{ fontSize: 10 }}>{server.mysql_user}@:{server.mysql_port || 3306}</Tag>}
        </Space>
      ),
      children: (
        <ServerMonitorPanel
          serverId={sId}
          serverName={label}
          serverIp={server?.ip || ''}
          interval={interval}
          autoRefresh={autoRefresh}
        />
      ),
    };
  });

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><DatabaseOutlined style={{ marginRight: 8 }} />MySQL Monitor</h1>
          <div className="page-subtitle" style={{ color: '#8392a5', fontSize: 12 }}>
            {selectedServers.length} servidor{selectedServers.length !== 1 ? 'es' : ''} seleccionado{selectedServers.length !== 1 ? 's' : ''}
          </div>
        </div>
        <Space wrap>
          <span style={{ fontSize: 12, color: '#8392a5' }}>Auto-refresh</span>
          <Switch checked={autoRefresh} onChange={setAutoRefresh} size="small" />
          {autoRefresh && (
            <Space.Compact size="small">
              {REFRESH_INTERVALS.map(({ label, value }) => (
                <Button key={value} type={interval === value ? 'primary' : 'default'}
                  onClick={() => setRefreshInterval(value)} size="small">
                  {label}
                </Button>
              ))}
            </Space.Compact>
          )}
        </Space>
      </div>

      <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CloudServerOutlined style={{ fontSize: 18, color: '#0168fa' }} />
          <Select
            mode="multiple"
            placeholder="Seleccionar servidores MySQL..."
            value={selectedServers}
            onChange={setSelectedServers}
            loading={loadingServers}
            style={{ flex: 1 }}
            maxTagCount="responsive"
            optionFilterProp="label"
            options={servers.map(s => ({
              value: s.id,
              label: `${s.nombre} (${s.ip})`,
            }))}
          />
        </div>
      </Card>

      {selectedServers.length === 0 ? (
        <Empty
          image={<DatabaseOutlined style={{ fontSize: 64, color: '#d1d5db' }} />}
          description={
            <span style={{ color: '#8392a5' }}>
              Selecciona uno o más servidores para monitorear.<br />
              <small>Configura credenciales MySQL en la edición de cada servidor.</small>
            </span>
          }
        />
      ) : selectedServers.length === 1 ? (
        <Card style={{ borderRadius: 12 }}>
          <ServerMonitorPanel
            serverId={selectedServers[0]}
            serverName={servers.find(s => s.id === selectedServers[0])?.nombre || ''}
            serverIp={servers.find(s => s.id === selectedServers[0])?.ip || ''}
            interval={interval}
            autoRefresh={autoRefresh}
          />
        </Card>
      ) : (
        <Collapse
          items={collapseItems}
          defaultActiveKey={selectedServers.map(String)}
          style={{ borderRadius: 12 }}
        />
      )}

      <style>{`
        .row-danger td { background: #fdf2f2 !important; }
        .row-warning td { background: #fffbeb !important; }
      `}</style>
    </div>
  );
};

export default MonitorMySQL;
